import React, { useEffect, useMemo, useState } from "react";
import BlockIcon from "@mui/icons-material/Block";
import CheckIcon from "@mui/icons-material/Check";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ExtensionIcon from "@mui/icons-material/Extension";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import StarsIcon from "@mui/icons-material/Stars";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useSearchParams } from "react-router-dom";
import { resolveScreenshotDevice } from "../../../components/ScreenshotDevicePicker";
import { useScreenshotTranslation } from "../../../lib/screenshotI18n";
import { resolveApiFetchUrl } from "../../../services/ApiBaseUrl";
import { apiFetch } from "../../../services/apiFetch";
import PlayBackground from "./PlayBackground";
import { screenshotIphoneBgSrc } from "../lib/screenshotIphoneBackgrounds";
import ScreenshotPlayPanel from "./ScreenshotPlayPanel";
import { WORD_WHEEL_QUEST_TITLE } from "../routes";
import { buildClueMapFromDisplayClue, normalizeWord } from "../lib/gridReveal";
import {
    WORD_WHEEL_SHELL_BG,
    WW_SHOT_CTA_FEATURE_SIZE,
    WW_SHOT_CTA_HEADLINE_SIZE,
    WW_SHOT_CTA_SUBTITLE_SIZE,
    WW_SHOT_HEADLINE_SIZE,
    WW_SHOT_SUBTITLE_SIZE,
} from "../styles/screenshotStyles";
import {
    SCREENSHOT_DESIGN_H as AD_PHONE_H,
    SCREENSHOT_DESIGN_W as AD_PHONE_W,
    screenshotDesignScaleFromViewport as phoneDesignScaleFromViewport,
} from "../../../components/ScreenshotDesignFrame";

/** Nested phone iframe URL — normal screenshot page (no ad-frame size params). */
function phoneHrefForGoogleAd() {
    if (typeof window === "undefined") return "";
    const url = new URL(window.location.href);
    url.searchParams.delete("googleAdW");
    url.searchParams.delete("googleAdH");
    url.searchParams.delete("googleAdPhone");
    url.searchParams.delete("googleAdLandscape");
    url.searchParams.delete("googleAdSquare");
    return `${url.pathname}${url.search}`;
}

const TITLE_STACK =
    '"Segoe UI", "Arial Black", "Helvetica Neue", Impact, sans-serif';

const WW = "mobileScreenshot.wordWheel";

const SEASON_TRACK_ID = "ww_season_2026";
const MASTER_TRACK_ID = "ww_master";
const MASTER_PATH_NODES = Array.from({ length: 12 }, (_, index) => ({
    n: index + 1,
    x: index % 2 === 0 ? 64 - (index % 4) * 4 : 34 + (index % 4) * 3,
    y: 6 + index * 6.5,
}));

function chapterCardTitle(chapter) {
    const raw = chapter?.title ? String(chapter.title).trim() : "";
    if (/^chapter\s+\d+/i.test(raw)) {
        return raw;
    }
    const no = chapter?.chapterNo;
    return no ? `Chapter ${no}: ${raw}` : raw;
}

export function JourneyMapColumn() {
    const [chapters, setChapters] = useState([]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const result = await apiFetch(
                    resolveApiFetchUrl(`/v1/journey/tracks/${SEASON_TRACK_ID}/map?includeLevels=false`),
                    { method: "GET" },
                );
                const json = await result.json();
                const list = Array.isArray(json?.chapters) ? json.chapters : [];
                if (!cancelled) {
                    setChapters(list.slice(0, 4));
                }
            } catch (error) {
                if (!cancelled) {
                    setChapters([]);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <Box
            sx={{
                flex: 1,
                minHeight: 0,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                px: "4cqw",
                containerType: "size",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", pt: "1.6cqh", pb: "1.2cqh" }}>
                <Box
                    sx={{
                        width: "4.2cqh",
                        height: "4.2cqh",
                        borderRadius: "50%",
                        bgcolor: "rgba(255,255,255,0.94)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
                        flexShrink: 0,
                    }}
                >
                    <ArrowBackIosNewIcon sx={{ fontSize: "1.8cqh", color: "#334155" }} />
                </Box>
                <Typography
                    sx={{
                        flex: 1,
                        textAlign: "center",
                        fontWeight: 700,
                        fontSize: "2.15cqh",
                        color: "#1e293b",
                        mr: "4.2cqh",
                        textShadow: "0 0 8px rgba(255,255,255,0.95), 0 1px 0 #fff",
                    }}
                >
                    Journey map
                </Typography>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column", alignItems: "center" }}>
                {chapters.map((chapter, index) => {
                    const image = chapter.imageUrl400 || chapter.imageUrl || "";
                    return (
                        <Box key={chapter.chapterNo || index} sx={{ width: "86%" }}>
                            <Box
                                sx={{
                                    bgcolor: "#fff",
                                    borderRadius: "2.2cqh",
                                    p: "0.7cqh",
                                    boxShadow: "0 8px 18px rgba(15,23,42,0.22)",
                                }}
                            >
                                {image ? (
                                    <Box
                                        component="img"
                                        src={image}
                                        alt=""
                                        sx={{
                                            display: "block",
                                            width: "100%",
                                            height: "16.5cqh",
                                            objectFit: "cover",
                                            borderRadius: "1.6cqh",
                                        }}
                                    />
                                ) : (
                                    <Box sx={{ width: "100%", height: "16.5cqh", borderRadius: "1.6cqh", bgcolor: "#e7e5e4" }} />
                                )}
                            </Box>
                            <Typography
                                sx={{
                                    mt: "0.7cqh",
                                    px: "1cqw",
                                    textAlign: "center",
                                    fontWeight: 800,
                                    fontSize: "1.9cqh",
                                    lineHeight: 1.15,
                                    color: "#1c1917",
                                    textShadow: "0 0 8px rgba(255,255,255,0.95), 0 0 2px rgba(255,255,255,0.95), 0 1px 0 #fff",
                                }}
                            >
                                {chapterCardTitle(chapter)}
                            </Typography>
                            {index < chapters.length - 1 ? (
                                <Box sx={{ display: "flex", justifyContent: "center", mt: "0.2cqh", mb: "0.45cqh" }}>
                                    <KeyboardArrowDownIcon
                                        sx={{
                                            fontSize: "3.1cqh",
                                            color: "#fff",
                                            filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.85)) drop-shadow(0 0 2px rgba(0,0,0,0.65))",
                                        }}
                                    />
                                </Box>
                            ) : null}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

const HERO_BADGES = [
    { key: "badge-daily-puzzles", Icon: TimerOutlinedIcon },
    { key: "badge-1000-levels", Icon: ExtensionIcon },
    { key: "badge-smart-hints", Icon: LightbulbOutlinedIcon },
];

const STORE_CARDS = [
    { key: "store-classic", Icon: ExtensionIcon },
    { key: "store-hard", Icon: StarsIcon },
    { key: "store-master", Icon: EmojiEventsIcon },
    { key: "store-bundles", Icon: LayersOutlinedIcon },
    { key: "store-coins", Icon: MonetizationOnOutlinedIcon },
    { key: "store-ad-free", Icon: BlockIcon },
];

const DICT_BULLETS = ["dict-bullet-1", "dict-bullet-2", "dict-bullet-3"];

/** PC 1920×1080: three columns, scene content in the center column. */
function PcCenterStage({ enabled, children }) {
    if (!enabled) return children;
    return (
        <Box
            sx={{
                flex: 1,
                minHeight: 0,
                width: "100%",
                height: "100%",
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)",
                alignItems: "stretch",
            }}
        >
            <Box />
            <Box
                sx={{
                    minWidth: 0,
                    minHeight: 0,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {children}
            </Box>
            <Box />
        </Box>
    );
}

/** Landscape PC screen chrome (16:9), used by the themes shot. */
function PcScreenFrame({ children }) {
    return (
        <Box
            sx={{
                width: "100%",
                aspectRatio: "16 / 9",
                borderRadius: "14px",
                p: "6px",
                background:
                    "linear-gradient(160deg, rgba(226,232,240,0.7) 0%, rgba(15,23,42,0.94) 42%, rgba(56,189,248,0.35) 100%)",
                boxShadow: "0 18px 40px rgba(0,0,0,0.38), 0 0 0 1px rgba(186,230,253,0.28)",
                boxSizing: "border-box",
            }}
        >
            <Box
                sx={{
                    borderRadius: "9px",
                    overflow: "hidden",
                    bgcolor: "#0f172a",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {children}
            </Box>
        </Box>
    );
}

/** Soft device chrome — tall portrait that fills available height. */
function PhoneFrame({ rotate = 0, children, sx = {} }) {
    return (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                transform: rotate ? `rotate(${rotate}deg)` : "none",
                transformOrigin: "center center",
                borderRadius: "28px",
                p: "6px",
                background:
                    "linear-gradient(160deg, rgba(226,232,240,0.55) 0%, rgba(15,23,42,0.92) 45%, rgba(56,189,248,0.28) 100%)",
                boxShadow:
                    "0 20px 48px rgba(0,0,0,0.35), 0 0 0 1px rgba(186,230,253,0.22)",
                boxSizing: "border-box",
                ...sx,
            }}
        >
            <Box
                sx={{
                    borderRadius: "22px",
                    overflow: "hidden",
                    bgcolor: "#0f172a",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {children}
            </Box>
        </Box>
    );
}

/**
 * Full-bleed viewport — no black letterbox. Use a ~9:16 capture window.
 * With `?googleAdW=&googleAdH=` (Google Ads only), expands this shell background to the
 * ad size and nests the normal portrait screenshot so UI ratio is unchanged.
 * Optional `googleAdBleed` paints behind the phone (e.g. play scene photo for 02/03).
 */
function Shell({ children, bg, googleAdBleed = null }) {
    const [searchParams] = useSearchParams();
    const adW = Number(searchParams.get("googleAdW")) || 0;
    const adH = Number(searchParams.get("googleAdH")) || 0;
    const isGoogleAd = adW > 0 && adH > 0;
    const fitScale = isGoogleAd ? Math.min(adW / AD_PHONE_W, adH / AD_PHONE_H) : 1;
    const phoneSrc = useMemo(
        () => (isGoogleAd ? phoneHrefForGoogleAd() : ""),
        [isGoogleAd, searchParams],
    );
    const [phoneFrame, setPhoneFrame] = useState(() =>
        typeof window === "undefined"
            ? {
                  mode: "fill",
                  scale: 1,
                  designW: AD_PHONE_W,
                  designH: AD_PHONE_H,
              }
            : phoneDesignScaleFromViewport(window.innerWidth, window.innerHeight),
    );

    useEffect(() => {
        const prevHtml = document.documentElement.style.overflow;
        const prevBody = document.body.style.overflow;
        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
        const apply = () =>
            setPhoneFrame(phoneDesignScaleFromViewport(window.innerWidth, window.innerHeight));
        apply();
        window.addEventListener("resize", apply);
        return () => {
            document.documentElement.style.overflow = prevHtml;
            document.body.style.overflow = prevBody;
            window.removeEventListener("resize", apply);
        };
    }, []);

    if (isGoogleAd) {
        return (
            <Box
                data-screenshot-frame="word-wheel-screenshot"
                data-google-ad-shell="1"
                sx={{
                    position: "fixed",
                    inset: 0,
                    width: adW,
                    height: adH,
                    boxSizing: "border-box",
                    overflow: "hidden",
                    background: bg,
                    zIndex: 1,
                }}
            >
                {googleAdBleed ? (
                    <Box
                        aria-hidden
                        sx={{
                            position: "absolute",
                            inset: 0,
                            zIndex: 0,
                            overflow: "hidden",
                        }}
                    >
                        {googleAdBleed}
                    </Box>
                ) : null}
                <Box
                    component="iframe"
                    data-ww-ad-phone="1"
                    src={phoneSrc}
                    title="Word Wheel ad phone"
                    sx={{
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        width: AD_PHONE_W,
                        height: AD_PHONE_H,
                        border: 0,
                        transform: `translate(-50%, -50%) scale(${fitScale})`,
                        transformOrigin: "center center",
                        display: "block",
                        pointerEvents: "none",
                        zIndex: 1,
                    }}
                />
            </Box>
        );
    }

    return (
        <Box
            data-screenshot-frame="word-wheel-screenshot"
            sx={{
                position: "fixed",
                inset: 0,
                width: "100%",
                height: "100%",
                minHeight: "100dvh",
                boxSizing: "border-box",
                overflow: "hidden",
                background: bg,
                zIndex: 1,
            }}
        >
            {googleAdBleed ? (
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 0,
                        overflow: "hidden",
                    }}
                >
                    {googleAdBleed}
                </Box>
            ) : null}
            <Box
                data-ww-shot-frame={phoneFrame.mode}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    boxSizing: "border-box",
                    overflow: "hidden",
                    background: bg,
                    containerType: "size",
                    containerName: "ww-shot-shell",
                    zIndex: 1,
                    ...(phoneFrame.mode === "phone"
                        ? {
                              width: phoneFrame.designW,
                              height: phoneFrame.designH,
                              position: "absolute",
                              left: "50%",
                              top: "50%",
                              transform: `translate(-50%, -50%) scale(${phoneFrame.scale})`,
                              transformOrigin: "center center",
                          }
                        : {
                              width: "100%",
                              height: "100%",
                          }),
                }}
            >
                {children}
            </Box>
        </Box>
    );
}

function TopHeadline({ headline, subtitle, light = false }) {
    return (
        <Box
            sx={{
                flexShrink: 0,
                px: 2.5,
                pt: "max(env(safe-area-inset-top, 0px), 32px)",
                pb: 1.5,
                textAlign: "center",
            }}
        >
            <Typography
                component="h1"
                sx={{
                    m: 0,
                    fontFamily: TITLE_STACK,
                    fontWeight: 900,
                    fontSize: WW_SHOT_HEADLINE_SIZE,
                    lineHeight: 1.08,
                    letterSpacing: "0.03em",
                    color: light ? "#f8fafc" : "#0f172a",
                    textShadow: light ? "0 2px 18px rgba(2,6,23,0.55)" : "none",
                }}
            >
                {headline}
            </Typography>
            {subtitle ? (
                <Typography
                    sx={{
                        m: 0,
                        mt: 1,
                        fontWeight: 600,
                        fontSize: WW_SHOT_SUBTITLE_SIZE,
                        lineHeight: 1.3,
                        color: light ? "rgba(226,232,240,0.92)" : "#475569",
                    }}
                >
                    {subtitle}
                </Typography>
            ) : null}
        </Box>
    );
}

/** Tall play stage that fills remaining height (not a small middle square). */
function PlayStage({ children, rotate = 0, framed = true }) {
    return (
        <Box
            sx={{
                flex: 1,
                minHeight: 0,
                width: "100%",
                display: "flex",
                alignItems: "stretch",
                justifyContent: "center",
                px: framed ? 1.25 : 0,
                pb: framed
                    ? "max(env(safe-area-inset-bottom, 0px), 20px)"
                    : "max(env(safe-area-inset-bottom, 0px), 0px)",
                boxSizing: "border-box",
            }}
        >
            {framed ? (
                <Box sx={{ width: "100%", height: "100%", minHeight: 0 }}>
                    <PhoneFrame rotate={rotate}>{children}</PhoneFrame>
                </Box>
            ) : (
                <Box
                    sx={{
                        width: "100%",
                        height: "100%",
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                    }}
                >
                    {children}
                </Box>
            )}
        </Box>
    );
}

function FillPlayPanel({
    puzzle,
    foundWords,
    scene,
    background,
    theme,
    topInset = false,
    clueMode = false,
    compact = false,
    splitColumns = false,
    aboveClue = null,
}) {
    return (
        <Box
            sx={{
                flex: 1,
                minHeight: 0,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                "& > *": {
                    flex: 1,
                    minHeight: 0,
                    height: "100% !important",
                    borderRadius: "0 !important",
                },
            }}
        >
            <ScreenshotPlayPanel
                puzzle={puzzle}
                foundWords={foundWords}
                journeyLevel={scene?.journeyLevel}
                background={background || scene?.background || "deep_sea"}
                theme={theme || scene?.theme || "random"}
                fill
                compact={compact}
                topInset={splitColumns ? false : topInset}
                clueMode={clueMode}
                splitColumns={splitColumns}
                aboveClue={aboveClue}
            />
        </Box>
    );
}

/** 01 — Hero title card. */
export function HeroLayout({ headline, subtitle, background }) {
    const { t } = useScreenshotTranslation();
    return (
        <Shell bg={WORD_WHEEL_SHELL_BG.hero}>
            <Box
                sx={{
                    flexShrink: 0,
                    px: 2.5,
                    pt: "max(env(safe-area-inset-top, 0px), 40px)",
                    pb: 2,
                    textAlign: "center",
                }}
            >
                <Typography
                    component="h1"
                    sx={{
                        m: 0,
                        fontFamily: TITLE_STACK,
                        fontWeight: 900,
                        fontSize: "2.15rem",
                        lineHeight: 1.08,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        background:
                            "linear-gradient(180deg, #f8fafc 0%, #7dd3fc 40%, #38bdf8 70%, #e2e8f0 100%)",
                        WebkitBackgroundClip: "text",
                        backgroundClip: "text",
                        color: "transparent",
                        filter: "drop-shadow(0 0 16px rgba(56,189,248,0.55))",
                    }}
                >
                    {headline || WORD_WHEEL_QUEST_TITLE}
                </Typography>
                <Typography
                    sx={{
                        mt: 1.5,
                        mb: 2,
                        fontStyle: "italic",
                        fontWeight: 600,
                        fontSize: "1.05rem",
                        lineHeight: 1.35,
                        color: "rgba(186,230,253,0.92)",
                    }}
                >
                    {subtitle}
                </Typography>
                <Stack spacing={1} alignItems="center" sx={{ width: "100%" }}>
                    {HERO_BADGES.map(({ key, Icon }) => (
                        <Stack
                            key={key}
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{
                                maxWidth: "100%",
                                px: 1.5,
                                py: 0.7,
                                borderRadius: 999,
                                bgcolor: "rgba(2,6,23,0.55)",
                                border: "1px solid rgba(56,189,248,0.4)",
                            }}
                        >
                            <Icon sx={{ fontSize: "clamp(22px, 5.5cqmin, 34px)", color: "#7dd3fc", flexShrink: 0 }} />
                            <Typography
                                sx={{
                                    fontSize: "clamp(0.82rem, 3.2cqmin, 1.05rem)",
                                    fontWeight: 800,
                                    color: "#e0f2fe",
                                    lineHeight: 1.15,
                                    letterSpacing: "0.02em",
                                }}
                            >
                                {t(`${WW}.${key}`)}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>
            </Box>
            <PlayStage rotate={-4}>
                <Box
                    sx={{
                        position: "relative",
                        flex: 1,
                        minHeight: 0,
                        height: "100%",
                        containerType: "size",
                        containerName: "ww-hero-stage",
                    }}
                >
                    <PlayBackground background={background || "tropical_island"} theme="random" />
                </Box>
            </PlayStage>
        </Shell>
    );
}

/** 02 / 03 — full-bleed play (edge-to-edge); headline overlays the art. */
export function GameplayLayout({
    headline,
    subtitle,
    puzzle,
    foundWords,
    scene,
    edgeBleed = false,
}) {
    const [searchParams] = useSearchParams();
    const pcSplit = resolveScreenshotDevice(searchParams.get("device")) === "pc";
    const shellBg = edgeBleed
        ? WORD_WHEEL_SHELL_BG.progression
        : WORD_WHEEL_SHELL_BG.gameplay;
    const playBgKey =
        scene?.background || (edgeBleed ? "road" : "deep_sea");
    const playTheme = scene?.theme || "random";
    const sceneBleed = <PlayBackground background={playBgKey} theme={playTheme} />;

    if (pcSplit) {
        return (
            <Shell bg={shellBg} googleAdBleed={sceneBleed}>
                <TopHeadline headline={headline} subtitle={subtitle} light />
                <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", position: "relative" }}>
                    <FillPlayPanel
                        puzzle={puzzle}
                        foundWords={foundWords}
                        scene={scene}
                        splitColumns
                    />
                </Box>
            </Shell>
        );
    }

    return (
        <Shell bg={shellBg} googleAdBleed={sceneBleed}>
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    width: "100%",
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {/* Play art fills the entire frame (including under the headline). */}
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 0,
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <FillPlayPanel
                        puzzle={puzzle}
                        foundWords={foundWords}
                        scene={scene}
                        topInset
                    />
                </Box>
                <Box
                    sx={{
                        position: "relative",
                        zIndex: 2,
                        flexShrink: 0,
                        pointerEvents: "none",
                        background:
                            "linear-gradient(180deg, rgba(2,6,23,0.45) 0%, rgba(2,6,23,0.12) 70%, transparent 100%)",
                    }}
                >
                    <TopHeadline headline={headline} subtitle={subtitle} light />
                </Box>
            </Box>
        </Shell>
    );
}

/** 02 — Clue mechanic: meaning first, word still hidden on the grid. */
export function ClueLayout({ headline, subtitle, puzzle, foundWords, scene }) {
    const [searchParams] = useSearchParams();
    const pcSplit = resolveScreenshotDevice(searchParams.get("device")) === "pc";
    const playBgKey = scene?.background || "deep_sea";
    const playTheme = scene?.theme || "random";
    const sceneBleed = <PlayBackground background={playBgKey} theme={playTheme} />;

    if (pcSplit) {
        return (
            <Shell bg={WORD_WHEEL_SHELL_BG.gameplay} googleAdBleed={sceneBleed}>
                <TopHeadline headline={headline} subtitle={subtitle} light />
                <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                    <FillPlayPanel
                        puzzle={puzzle}
                        foundWords={foundWords}
                        scene={scene}
                        splitColumns
                        clueMode
                    />
                </Box>
            </Shell>
        );
    }

    return (
        <Shell bg={WORD_WHEEL_SHELL_BG.gameplay} googleAdBleed={sceneBleed}>
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    width: "100%",
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 0,
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <FillPlayPanel
                        puzzle={puzzle}
                        foundWords={foundWords}
                        scene={scene}
                        topInset
                        clueMode
                    />
                </Box>
                <Box
                    sx={{
                        position: "relative",
                        zIndex: 2,
                        flexShrink: 0,
                        pointerEvents: "none",
                        background:
                            "linear-gradient(180deg, rgba(2,6,23,0.55) 0%, rgba(2,6,23,0.16) 72%, transparent 100%)",
                    }}
                >
                    <TopHeadline headline={headline} subtitle={subtitle} light />
                </Box>
            </Box>
        </Shell>
    );
}

/** 02 — Season journey map: chapter cards on the scene photo. */
export function JourneyLayout({ scene }) {
    const [searchParams] = useSearchParams();
    const pcSplit = resolveScreenshotDevice(searchParams.get("device")) === "pc";
    const playBgKey = scene?.background || "iphone_2";
    return (
        <Shell bg={WORD_WHEEL_SHELL_BG.journey}>
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    width: "100%",
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Box sx={{ position: "absolute", inset: 0, zIndex: 0 }}>
                    <PlayBackground background={playBgKey} theme={scene?.theme || "random"} />
                </Box>
                <Box sx={{ position: "relative", zIndex: 1, flex: 1, minHeight: 0, display: "flex" }}>
                    <PcCenterStage enabled={pcSplit}>
                        <JourneyMapColumn />
                    </PcCenterStage>
                </Box>
            </Box>
        </Shell>
    );
}

/** 08 — Master journey: locked chapter, path, and unlock button. */
export function MasterJourneyColumn() {
    const [chapter, setChapter] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const result = await apiFetch(
                    resolveApiFetchUrl(`/v1/journey/tracks/${MASTER_TRACK_ID}/map?includeLevels=false`),
                    { method: "GET" },
                );
                const json = await result.json();
                const list = Array.isArray(json?.chapters) ? json.chapters : [];
                if (!cancelled) {
                    setChapter(list[0] || null);
                }
            } catch (error) {
                if (!cancelled) {
                    setChapter(null);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const image = chapter?.imageUrlGray || chapter?.imageUrl400 || chapter?.imageUrl || "";
    const chapterTitle = chapter?.title ? String(chapter.title) : "Play a game";

    return (
        <Box
            sx={{
                flex: 1,
                minHeight: 0,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                px: "5cqw",
                pb: "1.2cqh",
                containerType: "size",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", pt: "1.4cqh", pb: "0.6cqh" }}>
                <Box
                    sx={{
                        width: "4cqh",
                        height: "4cqh",
                        borderRadius: "50%",
                        bgcolor: "rgba(255,255,255,0.94)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
                        flexShrink: 0,
                    }}
                >
                    <ArrowBackIosNewIcon sx={{ fontSize: "1.7cqh", color: "#334155" }} />
                </Box>
                <Typography
                    sx={{
                        flex: 1,
                        textAlign: "center",
                        fontWeight: 800,
                        fontSize: "2.2cqh",
                        color: "#1c1917",
                        mr: "4cqh",
                        textShadow: "0 0 8px rgba(255,255,255,0.95), 0 1px 0 #fff",
                    }}
                >
                    Master
                </Typography>
            </Box>
            <Typography
                sx={{
                    textAlign: "center",
                    fontWeight: 800,
                    fontSize: "2cqh",
                    lineHeight: 1.15,
                    color: "#1c1917",
                    textShadow: "0 0 8px rgba(255,255,255,0.95), 0 1px 0 #fff",
                }}
            >
                The Ultimate Journey
            </Typography>
            <Typography
                sx={{
                    mt: "0.45cqh",
                    px: "2cqw",
                    textAlign: "center",
                    fontWeight: 700,
                    fontSize: "1.45cqh",
                    lineHeight: 1.25,
                    color: "#1c1917",
                    textShadow: "0 0 8px rgba(255,255,255,0.95), 0 1px 0 #fff",
                }}
            >
                Starts gentle, then builds into serious brain-teasers. A long, rewarding challenge designed for true word masters!
            </Typography>
            <Box sx={{ width: "74%", mx: "auto", mt: "1.1cqh" }}>
                <Box
                    sx={{
                        position: "relative",
                        bgcolor: "#fff",
                        borderRadius: "1.8cqh",
                        p: "0.55cqh",
                        boxShadow: "0 8px 18px rgba(15,23,42,0.22)",
                    }}
                >
                    {image ? (
                        <Box
                            component="img"
                            src={image}
                            alt=""
                            sx={{
                                display: "block",
                                width: "100%",
                                height: "13cqh",
                                objectFit: "cover",
                                borderRadius: "1.3cqh",
                                filter: chapter?.imageUrlGray ? "none" : "grayscale(1)",
                            }}
                        />
                    ) : (
                        <Box sx={{ width: "100%", height: "13cqh", borderRadius: "1.3cqh", bgcolor: "#e7e5e4" }} />
                    )}
                    <Box
                        sx={{
                            position: "absolute",
                            right: "1.1cqh",
                            bottom: "1.1cqh",
                            width: "2.6cqh",
                            height: "2.6cqh",
                            borderRadius: "50%",
                            bgcolor: "rgba(255,255,255,0.92)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
                        }}
                    >
                        <LockOutlinedIcon sx={{ fontSize: "1.5cqh", color: "#57534e" }} />
                    </Box>
                </Box>
                <Typography
                    sx={{
                        mt: "0.45cqh",
                        textAlign: "center",
                        fontWeight: 800,
                        fontSize: "1.7cqh",
                        lineHeight: 1.15,
                        color: "#1c1917",
                        textShadow: "0 0 8px rgba(255,255,255,0.95), 0 1px 0 #fff",
                    }}
                >
                    {chapterTitle}
                </Typography>
            </Box>
            <Box sx={{ position: "relative", flex: 1, minHeight: 0, mx: "4cqw", mt: "0.2cqh", overflow: "hidden" }}>
                <Box sx={{ position: "absolute", left: 0, right: 0, top: 0, height: "210%" }}>
                <Box
                    component="svg"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                    sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", overflow: "visible" }}
                >
                    <polyline
                        points={MASTER_PATH_NODES.map((node) => `${node.x},${node.y}`).join(" ")}
                        fill="none"
                        stroke="rgba(255,255,255,0.92)"
                        strokeWidth="1.4"
                        strokeDasharray="2.2 2.2"
                        strokeLinecap="round"
                    />
                </Box>
                {MASTER_PATH_NODES.map((node) => (
                    <Box
                        key={node.n}
                        sx={{
                            position: "absolute",
                            left: `${node.x}%`,
                            top: `${node.y}%`,
                            transform: "translate(-50%, -50%)",
                            width: "5.4cqh",
                            height: "5.4cqh",
                        }}
                    >
                        <Box
                            sx={{
                                position: "absolute",
                                left: "50%",
                                top: "-1.5cqh",
                                transform: "translateX(-50%)",
                                width: "2.2cqh",
                                height: "2.2cqh",
                                borderRadius: "50%",
                                bgcolor: "rgba(255,255,255,0.92)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                            }}
                        >
                            <LockOutlinedIcon sx={{ fontSize: "1.25cqh", color: "#57534e" }} />
                        </Box>
                        <Box
                            sx={{
                                width: "100%",
                                height: "100%",
                                borderRadius: "50%",
                                bgcolor: "#e7e5e4",
                                border: "2px solid rgba(255,255,255,0.85)",
                                boxShadow: "0 4px 10px rgba(15,23,42,0.18)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: 800,
                                fontSize: "1.8cqh",
                                color: "#44403c",
                            }}
                        >
                            {node.n}
                        </Box>
                    </Box>
                ))}
                </Box>
            </Box>
            <Box
                sx={{
                    mx: "1cqw",
                    py: "1.15cqh",
                    borderRadius: "1.5cqh",
                    bgcolor: "#e8782f",
                    color: "#fff",
                    textAlign: "center",
                    fontWeight: 800,
                    fontSize: "1.9cqh",
                    boxShadow: "0 8px 16px rgba(0,0,0,0.28)",
                }}
            >
                Unlock this journey
            </Box>
        </Box>
    );
}

export function MasterJourneyLayout({ scene }) {
    const [searchParams] = useSearchParams();
    const pcSplit = resolveScreenshotDevice(searchParams.get("device")) === "pc";
    const playBgKey = scene?.background || "iphone_2";
    return (
        <Shell bg={WORD_WHEEL_SHELL_BG.journey}>
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    width: "100%",
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Box sx={{ position: "absolute", inset: 0, zIndex: 0 }}>
                    <PlayBackground background={playBgKey} theme={scene?.theme || "random"} />
                </Box>
                <Box sx={{ position: "relative", zIndex: 1, flex: 1, minHeight: 0, display: "flex" }}>
                    <PcCenterStage enabled={pcSplit}>
                        <MasterJourneyColumn />
                    </PcCenterStage>
                </Box>
            </Box>
        </Shell>
    );
}

/** 05 — Dictionary highlight (uses real puzzle clue when available). */
export function DictionaryLayout({ headline, subtitle, puzzle, foundWords, scene }) {
    const { t } = useScreenshotTranslation();
    const requested = scene?.dictionaryWord ? normalizeWord(scene.dictionaryWord) : "";
    const sampleWord = requested || foundWords?.[0] || "MEND";
    const clueMap = useMemo(
        () => buildClueMapFromDisplayClue(puzzle?.displayClue),
        [puzzle?.displayClue],
    );
    const definition =
        clueMap.get(normalizeWord(sampleWord)) ||
        "A word discovered on your season journey — tap any found word to look it up.";
    const posLabel = definition.match(/^(noun|verb|adjective|adverb)\b/i)?.[1] || "word";
    const [searchParams] = useSearchParams();
    const pcSplit = resolveScreenshotDevice(searchParams.get("device")) === "pc";
    const dictionaryCopy = (
        <Box sx={{ px: 0.5, py: 0.25 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 15, color: "#f8fafc", mb: 0.5, textShadow: "0 2px 8px rgba(2,6,23,0.85)" }}>
                {String(sampleWord).toUpperCase()}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#bae6fd", fontWeight: 600, mb: 0.75, textShadow: "0 2px 8px rgba(2,6,23,0.85)" }}>
                {String(posLabel).toLowerCase()}
            </Typography>
            <Typography sx={{ fontSize: 13, lineHeight: 1.45, color: "#e0f2fe", textShadow: "0 2px 8px rgba(2,6,23,0.85)" }}>
                {definition}
            </Typography>
        </Box>
    );

    if (pcSplit) {
        return (
            <Shell bg={WORD_WHEEL_SHELL_BG.dictionary}>
                <TopHeadline headline={headline} subtitle={subtitle} />
                <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                    <FillPlayPanel
                        puzzle={puzzle}
                        foundWords={foundWords}
                        scene={scene}
                        splitColumns
                        aboveClue={dictionaryCopy}
                    />
                </Box>
            </Shell>
        );
    }

    return (
        <Shell bg={WORD_WHEEL_SHELL_BG.dictionary}>
            <TopHeadline headline={headline} subtitle={subtitle} />
            <Box
                sx={{
                    flexShrink: 0,
                    px: 1.5,
                    pb: 1.25,
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    gap: 1,
                }}
            >
                {DICT_BULLETS.map((key) => (
                    <Typography
                        key={key}
                        sx={{
                            flex: "1 1 0",
                            minWidth: 0,
                            textAlign: "center",
                            fontSize: 12,
                            fontWeight: 700,
                            lineHeight: 1.25,
                            color: "#0f172a",
                        }}
                    >
                        {t(`${WW}.${key}`)}
                    </Typography>
                ))}
            </Box>
            <PlayStage>
                <Box sx={{ position: "relative", flex: 1, height: "100%", minHeight: 0 }}>
                    <FillPlayPanel puzzle={puzzle} foundWords={foundWords} scene={scene} />
                    <Box
                        sx={{
                            position: "absolute",
                            left: 16,
                            right: 16,
                            bottom: 20,
                            zIndex: 5,
                        }}
                    >
                        <Typography sx={{ fontWeight: 800, fontSize: 18, color: "#f8fafc", textShadow: "0 2px 8px rgba(2,6,23,0.85)" }}>
                            {String(sampleWord).toUpperCase()}
                        </Typography>
                        <Typography sx={{ mt: 0.5, fontSize: 14, lineHeight: 1.4, color: "#e0f2fe", textShadow: "0 2px 8px rgba(2,6,23,0.85)" }}>
                            {definition}
                        </Typography>
                    </Box>
                </Box>
            </PlayStage>
        </Shell>
    );
}

/** 06 — Three theme phones cascade. */
export function ThemesLayout({ headline, subtitle, puzzle, foundWords, scene }) {
    const [searchParams] = useSearchParams();
    const pcSplit = resolveScreenshotDevice(searchParams.get("device")) === "pc";
    const cascade = scene?.themeCascade || [
        { background: "iphone_4", theme: "random" },
        { background: "iphone_5", theme: "random" },
        { background: "iphone_6", theme: "random" },
    ];
    if (pcSplit) {
        return (
            <Shell bg={WORD_WHEEL_SHELL_BG.themes}>
                <TopHeadline headline={headline} subtitle={subtitle} light />
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        alignItems: "center",
                        gap: 2.5,
                        px: 3,
                        pb: 3,
                    }}
                >
                    {cascade.map((cfg, i) => (
                        <Box key={`${cfg.background}-${i}`} sx={{ flex: 1, minWidth: 0 }}>
                            <PcScreenFrame>
                                <FillPlayPanel
                                    puzzle={puzzle}
                                    foundWords={foundWords}
                                    scene={scene}
                                    background={cfg.background}
                                    theme={cfg.theme}
                                    splitColumns
                                />
                            </PcScreenFrame>
                        </Box>
                    ))}
                </Box>
            </Shell>
        );
    }
    return (
        <Shell bg={WORD_WHEEL_SHELL_BG.themes}>
            <TopHeadline headline={headline} subtitle={subtitle} light />
            <Box
                sx={{
                    flex: 1,
                    position: "relative",
                    minHeight: 0,
                    mx: 0.5,
                    mb: "max(env(safe-area-inset-bottom, 0px), 8px)",
                }}
            >
                {cascade.map((cfg, i) => {
                    const layouts = [
                        { left: "1%", top: "5%", rotate: -8, z: 2 },
                        { left: "24%", top: "1%", rotate: 2, z: 3 },
                        { left: "47%", top: "6%", rotate: 9, z: 4 },
                    ];
                    const pos = layouts[i] || layouts[0];
                    return (
                        <Box
                            key={`${cfg.background}-${i}`}
                            sx={{
                                position: "absolute",
                                left: pos.left,
                                top: pos.top,
                                height: "88%",
                                width: "auto",
                                aspectRatio: "9 / 19.5",
                                maxWidth: "50%",
                                zIndex: pos.z,
                            }}
                        >
                            <PhoneFrame rotate={pos.rotate}>
                                <FillPlayPanel
                                    puzzle={puzzle}
                                    foundWords={foundWords}
                                    scene={scene}
                                    background={cfg.background}
                                    theme={cfg.theme}
                                />
                            </PhoneFrame>
                        </Box>
                    );
                })}
            </Box>
        </Shell>
    );
}

/** 07 — Daily challenges (month calendar with day numbers). */
export function DailyLayout({ headline, subtitle }) {
    const { t } = useScreenshotTranslation();
    const weekdayLabels = useMemo(
        () => [0, 1, 2, 3, 4, 5, 6].map((i) => t(`${WW}.daily-weekday-${i}`)),
        [t],
    );
    // July 2026 calendar: Wed=1 … (matches WORD_WHEEL daily epoch era).
    // Grid cells: null = empty, number = date. Highlight 1–14 done, 15 today, rest upcoming.
    const monthCells = useMemo(() => {
        const leadingBlanks = 3; // July 2026 starts Wednesday
        const daysInMonth = 31;
        const today = 15;
        const cells = [];
        for (let i = 0; i < leadingBlanks; i += 1) cells.push(null);
        for (let d = 1; d <= daysInMonth; d += 1) {
            let state = "upcoming";
            if (d < today) state = "done";
            else if (d === today) state = "today";
            cells.push({ day: d, state });
        }
        while (cells.length % 7 !== 0) cells.push(null);
        return cells;
    }, []);
    const [searchParams] = useSearchParams();
    const pcSplit = resolveScreenshotDevice(searchParams.get("device")) === "pc";
    const coins = useMemo(
        () => [
            { top: "18%", left: "6%", size: 18 },
            { top: "30%", left: "88%", size: 15 },
            { top: "62%", left: "8%", size: 16 },
            { top: "74%", left: "84%", size: 14 },
        ],
        [],
    );
    return (
        <Shell bg={WORD_WHEEL_SHELL_BG.daily}>
            <PcCenterStage enabled={pcSplit}>
            <TopHeadline headline={headline} subtitle={subtitle} light />
            {coins.map((c, i) => (
                <Box
                    key={i}
                    aria-hidden
                    sx={{
                        position: "absolute",
                        top: c.top,
                        left: c.left,
                        width: c.size,
                        height: c.size,
                        borderRadius: "50%",
                        background: "radial-gradient(circle at 30% 30%, #fde68a, #f59e0b 60%, #b45309)",
                        boxShadow: "0 0 12px rgba(245,158,11,0.65)",
                        zIndex: 0,
                        opacity: 0.85,
                    }}
                />
            ))}
            <PlayStage rotate={pcSplit ? 0 : -2}>
                    <Box
                        sx={{
                            flex: 1,
                            height: "100%",
                            minHeight: 0,
                            p: "clamp(10px, 2.6cqmin, 20px)",
                            background:
                                "linear-gradient(180deg, #0c4a6e 0%, #082f49 45%, #020617 100%)",
                            color: "#e0f2fe",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "flex-start",
                            gap: "clamp(6px, 1.2cqmin, 10px)",
                            containerType: "size",
                            containerName: "ww-daily-stage",
                            boxSizing: "border-box",
                        }}
                    >
                    <Typography
                        sx={{
                            fontWeight: 800,
                            fontSize: "clamp(1.15rem, 5.5cqmin, 2rem)",
                            textAlign: "center",
                            flexShrink: 0,
                        }}
                    >
                        {t(`${WW}.daily-title`)}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: "clamp(12px, 3.2cqmin, 18px)",
                            textAlign: "center",
                            color: "rgba(186,230,253,0.75)",
                            flexShrink: 0,
                            mb: "clamp(2px, 0.6cqmin, 6px)",
                        }}
                    >
                        {t(`${WW}.daily-subtitle`)}
                    </Typography>

                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            width: "100%",
                            alignSelf: "stretch",
                            borderRadius: 2.5,
                            p: "clamp(8px, 2cqmin, 14px)",
                            bgcolor: "rgba(15,23,42,0.45)",
                            border: "1px solid rgba(125,211,252,0.28)",
                            display: "flex",
                            flexDirection: "column",
                            boxSizing: "border-box",
                        }}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ mb: 1, px: 0.25, flexShrink: 0 }}
                        >
                            <Typography sx={{ fontWeight: 800, fontSize: 15, color: "#e0f2fe" }}>
                                {t(`${WW}.daily-month-label`)}
                            </Typography>
                            <Box
                                sx={{
                                    px: 1.1,
                                    py: 0.35,
                                    borderRadius: 999,
                                    bgcolor: "rgba(251,191,36,0.2)",
                                    border: "1px solid rgba(251,191,36,0.45)",
                                    fontSize: 11,
                                    fontWeight: 800,
                                    color: "#fde68a",
                                }}
                            >
                                {t(`${WW}.daily-today`)}
                            </Box>
                        </Stack>

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "repeat(7, 1fr)",
                                gap: 0.4,
                                mb: 0.5,
                                flexShrink: 0,
                            }}
                        >
                            {weekdayLabels.map((label, i) => (
                                <Typography
                                    key={`${label}-${i}`}
                                    sx={{
                                        textAlign: "center",
                                        fontSize: 10,
                                        fontWeight: 700,
                                        color: "rgba(186,230,253,0.55)",
                                        letterSpacing: "0.04em",
                                    }}
                                >
                                    {label}
                                </Typography>
                            ))}
                        </Box>

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                                gridTemplateRows: "repeat(5, minmax(0, 1fr))",
                                gap: 0.5,
                                flex: 1,
                                minHeight: 0,
                                alignContent: "stretch",
                            }}
                        >
                            {monthCells.map((cell, i) => {
                                if (!cell) {
                                    return <Box key={`empty-${i}`} sx={{ minHeight: 0 }} />;
                                }
                                const { day, state } = cell;
                                const isToday = state === "today";
                                const isDone = state === "done";
                                return (
                                    <Box
                                        key={day}
                                        sx={{
                                            minHeight: 0,
                                            borderRadius: 1.25,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            containerType: "size",
                                            bgcolor: isToday
                                                ? "rgba(251,191,36,0.28)"
                                                : isDone
                                                  ? "rgba(34,197,94,0.32)"
                                                  : "rgba(148,163,184,0.12)",
                                            border: isToday
                                                ? "2px solid #fbbf24"
                                                : isDone
                                                  ? "1px solid rgba(74,222,128,0.5)"
                                                  : "1px solid rgba(125,211,252,0.18)",
                                            color: isToday
                                                ? "#fef3c7"
                                                : isDone
                                                  ? "#ecfdf5"
                                                  : "rgba(226,232,240,0.75)",
                                            boxShadow: isToday
                                                ? "0 0 12px rgba(251,191,36,0.35)"
                                                : "none",
                                        }}
                                    >
                                        {isDone ? (
                                            <CheckIcon
                                                aria-hidden
                                                sx={{
                                                    // Scale with the day cell — larger phones → bigger check.
                                                    fontSize: "clamp(14px, 58cqmin, 48px)",
                                                    color: "#ecfdf5",
                                                    filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.35))",
                                                }}
                                            />
                                        ) : (
                                            <Box
                                                component="span"
                                                sx={{
                                                    fontWeight: 800,
                                                    lineHeight: 1,
                                                    fontSize: "clamp(11px, 38cqmin, 28px)",
                                                }}
                                            >
                                                {day}
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>

                    <Stack
                        spacing="clamp(8px, 1.8cqmin, 12px)"
                        sx={{
                            mt: "clamp(6px, 1.4cqmin, 10px)",
                            flexShrink: 0,
                            width: "100%",
                            alignSelf: "stretch",
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing="clamp(10px, 2.5cqmin, 18px)"
                            alignItems="center"
                            sx={{
                                p: "clamp(12px, 3.2cqmin, 22px)",
                                borderRadius: "clamp(10px, 2cqmin, 16px)",
                                bgcolor: "rgba(251,191,36,0.15)",
                                border: "1px solid rgba(251,191,36,0.4)",
                                minHeight: "clamp(56px, 14cqmin, 96px)",
                            }}
                        >
                            <EmojiEventsIcon
                                sx={{
                                    color: "#fbbf24",
                                    fontSize: "clamp(28px, 9cqmin, 56px)",
                                    flexShrink: 0,
                                }}
                            />
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: "clamp(15px, 4.6cqmin, 28px)",
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {t(`${WW}.daily-streak-title`)}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: "clamp(12px, 3.4cqmin, 20px)",
                                        color: "rgba(254,243,199,0.8)",
                                        lineHeight: 1.3,
                                        mt: 0.25,
                                    }}
                                >
                                    {t(`${WW}.daily-streak-sub`)}
                                </Typography>
                            </Box>
                        </Stack>
                        <Stack
                            direction="row"
                            spacing="clamp(10px, 2.5cqmin, 18px)"
                            alignItems="center"
                            sx={{
                                p: "clamp(12px, 3.2cqmin, 22px)",
                                borderRadius: "clamp(10px, 2cqmin, 16px)",
                                bgcolor: "rgba(56,189,248,0.12)",
                                border: "1px solid rgba(56,189,248,0.35)",
                                minHeight: "clamp(56px, 14cqmin, 96px)",
                            }}
                        >
                            <StarsIcon
                                sx={{
                                    color: "#7dd3fc",
                                    fontSize: "clamp(28px, 9cqmin, 56px)",
                                    flexShrink: 0,
                                }}
                            />
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: "clamp(15px, 4.6cqmin, 28px)",
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {t(`${WW}.daily-event-title`)}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: "clamp(12px, 3.4cqmin, 20px)",
                                        color: "rgba(186,230,253,0.8)",
                                        lineHeight: 1.3,
                                        mt: 0.25,
                                    }}
                                >
                                    {t(`${WW}.daily-event-sub`)}
                                </Typography>
                            </Box>
                        </Stack>
                    </Stack>
                </Box>
            </PlayStage>
            </PcCenterStage>
        </Shell>
    );
}

/** 08 — Store hub: product names only, no prices. */
export function CtaLayout({ headline, subtitle, scene }) {
    const { t } = useScreenshotTranslation();
    const [searchParams] = useSearchParams();
    const pcSplit = resolveScreenshotDevice(searchParams.get("device")) === "pc";
    const photo = screenshotIphoneBgSrc(scene?.background, "");
    return (
        <Shell bg={WORD_WHEEL_SHELL_BG.cta}>
            <Box sx={{ position: "relative", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            {photo ? (
                <Box aria-hidden sx={{ position: "absolute", inset: 0, zIndex: 0 }}>
                    <PlayBackground background={photo} theme="random" showWash={false} />
                </Box>
            ) : null}
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
            <PcCenterStage enabled={pcSplit}>
            <Box
                sx={{
                    flexShrink: 0,
                    px: 2,
                    pt: "max(env(safe-area-inset-top, 0px), 28px)",
                    pb: 1.75,
                }}
            >
                <Typography
                    sx={{
                        fontFamily: TITLE_STACK,
                        fontWeight: 900,
                        fontSize: WW_SHOT_CTA_HEADLINE_SIZE,
                        lineHeight: 1.08,
                        color: "#f8fafc",
                        letterSpacing: "0.03em",
                        textAlign: "center",
                        mb: 1,
                        textShadow: "0 2px 12px rgba(2,6,23,0.85)",
                    }}
                >
                    {headline}
                </Typography>
                <Typography
                    sx={{
                        fontSize: WW_SHOT_CTA_SUBTITLE_SIZE,
                        fontWeight: 600,
                        lineHeight: 1.3,
                        color: "#f8fafc",
                        textAlign: "center",
                        mb: 1.75,
                        textShadow: "0 2px 10px rgba(2,6,23,0.85)",
                    }}
                >
                    {subtitle}
                </Typography>
            </Box>
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    px: 2,
                    pb: 1,
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 1.25,
                    alignContent: "center",
                }}
            >
                {STORE_CARDS.map(({ key, Icon }) => (
                    <Stack
                        key={key}
                        spacing={0.75}
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            minHeight: 72,
                            px: 0.5,
                            py: 1,
                            alignContent: "center",
                        }}
                    >
                        <Icon sx={{ fontSize: 32, color: "#7dd3fc" }} />
                        <Typography
                            sx={{
                                fontWeight: 900,
                                fontSize: "clamp(0.95rem, 3.6cqmin, 1.2rem)",
                                lineHeight: 1.1,
                                color: "#f8fafc",
                                textAlign: "center",
                            }}
                        >
                            {t(`${WW}.${key}`)}
                        </Typography>
                    </Stack>
                ))}
            </Box>
            </PcCenterStage>
            </Box>
            </Box>
        </Shell>
    );
}
